"use client";

import { Box, Flex } from "@chakra-ui/react";
import { Text } from "@/components/ui";
import { useInteractiveTokens, useThemeTokens } from "@/lib/theme/tokens";
import {
  getClassAvailabilityCompactLabel,
  type ClassAvailabilityDisplay,
  type ClassAvailabilityTone,
} from "@/lib/utils/classAvailability";

type AvailabilityBadgeSurface = "default" | "overlay";

const availabilityToneStyles = (
  theme: ReturnType<typeof useThemeTokens>,
  surface: AvailabilityBadgeSurface,
): Record<
  ClassAvailabilityTone,
  { bg: string; border: string; dot: string; text: string; accent?: string }
> => {
  if (surface === "overlay") {
    const chip = {
      bg: "rgba(11,13,16,0.86)",
      border: "rgba(255,255,255,0.22)",
      text: "rgba(255,255,255,0.96)",
    };
    return {
      open: {
        ...chip,
        dot: theme.accent.primary,
        accent: theme.accent.primary,
      },
      waitlist: {
        ...chip,
        dot: theme.accent.muted,
      },
      full: {
        ...chip,
        dot: "rgba(255,255,255,0.55)",
      },
      neutral: {
        ...chip,
        dot: theme.accent.muted,
      },
    };
  }

  return {
    open: {
      bg: theme.accent.mutedBg,
      border: theme.accent.mutedBg,
      dot: theme.accent.primary,
      text: theme.text.secondary,
      accent: theme.accent.primary,
    },
    waitlist: {
      bg: theme.accent.mutedBg,
      border: theme.accent.mutedBg,
      dot: theme.accent.muted,
      text: theme.text.primary,
    },
    full: {
      bg: theme.surfaceGlass.card,
      border: theme.borderGlass.subtle,
      dot: theme.text.tertiary,
      text: theme.text.secondary,
    },
    neutral: {
      bg: theme.surfaceGlass.card,
      border: theme.borderGlass.subtle,
      dot: theme.accent.muted,
      text: theme.text.secondary,
    },
  };
};

export function ClassAvailabilityBadge({
  availability,
  variant = "compact",
  surface = "default",
}: {
  availability: ClassAvailabilityDisplay;
  variant?: "compact" | "callout";
  /** Use overlay on photos so the label stays readable. */
  surface?: AvailabilityBadgeSurface;
}) {
  const theme = useThemeTokens();
  const interactive = useInteractiveTokens();
  const styles = availabilityToneStyles(theme, surface)[availability.tone];
  const spotsLeft = availability.spotsLeft;
  const compactLabel = getClassAvailabilityCompactLabel(availability);
  const isOverlay = surface === "overlay";

  return (
    <Flex
      align="center"
      gap={variant === "compact" ? 1.5 : 2.5}
      display="inline-flex"
      maxW="100%"
      px={variant === "compact" ? (isOverlay ? 3 : 2.5) : 3.5}
      py={variant === "compact" ? (isOverlay ? 1.5 : 1) : 2}
      mt={variant === "callout" ? "1px" : 0}
      borderRadius="full"
      borderWidth="1px"
      borderColor={styles.border}
      bg={styles.bg}
      backdropFilter={isOverlay ? interactive.backdrop.blurOverlay : undefined}
      boxShadow={
        isOverlay ? "0 1px 2px rgba(0,0,0,0.45), 0 8px 18px rgba(0,0,0,0.28)" : undefined
      }
    >
      <Box
        w="6px"
        h="6px"
        borderRadius="full"
        bg={styles.dot}
        flexShrink={0}
        boxShadow={`0 0 0 3px ${styles.bg}`}
      />
      <Text
        fontSize={variant === "compact" ? (isOverlay ? "sm" : "xs") : "sm"}
        fontWeight={isOverlay ? "600" : "500"}
        lineHeight="1.3"
        color={styles.text}
        whiteSpace="nowrap"
        textShadow={isOverlay ? "0 1px 2px rgba(0,0,0,0.45)" : undefined}
      >
        {variant === "callout" && spotsLeft != null && spotsLeft > 0 ? (
          <>
            <Text as="span" fontWeight="700" color={styles.accent ?? styles.text}>
              {spotsLeft}
            </Text>
            <Text as="span" color={styles.text}>
              {" "}
              {spotsLeft === 1 ? "spot" : "spots"} available
            </Text>
          </>
        ) : (
          variant === "callout" ? availability.label : compactLabel
        )}
      </Text>
    </Flex>
  );
}
